import { inject, Injectable, Injector } from '@angular/core';
import { AbstractTool } from '../../cross-team-shared/annotation/shared/abstract-tool';
import {
  isWithinTolerance,
  MouseButton,
} from '../../cross-team-shared/misc-services/events/mouse-event.service';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';
import {
  PointCloudContentState,
  PointCloudToolName,
} from '../state/point-cloud-content-state';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { MouseCoordinateService } from '../utils/mouse-coordinate.service';
import { RayCastService } from '../utils/ray-cast.service';
import { CameraControlDragHandler } from './camera-control-drag-handler';

@Injectable()
export abstract class AbstractPointCloudTool extends AbstractTool<
  PointCloudContentState,
  PointCloudContentStore
> {
  protected isDragging = false;
  protected override store = inject(PointCloudContentStore);
  protected rayCastService = inject(RayCastService);
  private helper?: CameraControlDragHandler;
  private lastRightMouseDownPosition: Point2D = [0, 0];
  private mouseCoordinateService = inject(MouseCoordinateService);

  abstract override readonly name: PointCloudToolName;

  constructor(protected injector: Injector) {
    super(injector);
  }

  override async onActivate() {
    await super.onActivate();

    // Often we try to register specific handlers in subclasses, rather than
    // registering everything in a superclass like this (and letting subclasses
    // override handlers). Watch for opportunities to move this toward that
    // pattern.
    this.onEvent('container', 'mousedown', this.onContainerMouseDown, {
      button: MouseButton.Middle,
      evenWithRegisteredHelper: true,
    });
    this.onEvent('container', 'mousedown', this.onContainerMouseDown, {
      button: MouseButton.Right,
      modifiers: ['shiftKey'],
      evenWithRegisteredHelper: true,
    });
    this.onEvent('container', 'mousedown', this.onContainerMouseDown, {
      button: MouseButton.Right,
      evenWithRegisteredHelper: true,
    });
    this.onEvent('container', 'mousemove', this.onContainerMouseMove, {
      evenWithRegisteredHelper: true,
    });
    this.onEvent('container', 'mouseout', this.onContainerMouseOut, {
      evenWithRegisteredHelper: true,
    });
    this.onEvent('container', 'mouseup', this.onContainerRightMouseUp, {
      evenWithRegisteredHelper: true,
      button: MouseButton.Right,
    });
  }

  protected onContainerMouseDown(event: MouseEvent) {
    this.lastRightMouseDownPosition = [event.x, event.y];
    this.helper = new CameraControlDragHandler(event, this.injector);
    this.registerHelper(this.helper);
    this.isDragging = true;
    void this.helper.completion.finally(() => {
      this.isDragging = false;
    });
  }

  protected onContainerMouseMove(event: MouseEvent) {
    if (event.buttons) {
      return;
    }

    this.mouseCoordinateService.update(event);
  }

  private onContainerMouseOut() {
    this.helper?.cancel();
  }

  private onContainerRightMouseUp(event: MouseEvent) {
    const shape = this.rayCastService.getIntersectingShape();
    if (
      isWithinTolerance(this.lastRightMouseDownPosition, [event.x, event.y])
    ) {
      if (shape) {
        this.shellConnector.modifyShapeSelection([shape.index], 'set', {
          source: 'context_menu',
        });
        this.shellConnector.openShapeContextMenu([
          event.clientX,
          event.clientY,
        ]);
      } else {
        this.shellConnector.openCanvasContextMenu([
          event.clientX,
          event.clientY,
        ]);
      }
    }
  }
}
